import React from 'react'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import Select from 'react-select'
import {
  BookingInterface,
  EventInterface,
  EventLocationInterface,
} from '../../calendar-interface'
import {
  EventLocationTypeEnum,
  EventTimeZoneTypeEnum,
  getReadableEventLocation,
  getReadableEventLocationIcon,
  TimeFormatEnum,
} from '../../constants'
import { GoogleMeetIcon } from '../../ui/icons/GoogleMeetIcon'
import { HourglassIcon } from '../../ui/icons/HourglassIcon'
import { WorldIcon2 } from '../../ui/icons/WorldIcon2'
import { formatDate, getAllTimeZones, getTimeZoneTime } from '../../utils'
import { CalendarEventDescriptionUi } from '../ui/calendar-event-description-ui'
import { EventInfoSkeleton } from './event-info-skeleton'
import { EventInfoWrapperUi } from './ui/event-info-wrapper-ui'
import { EventTimeZoneSelectWrapper } from './ui/event-timeZone-select-wrapper'
import { InputRadio } from './ui/input-radio'

interface EventInfoProps extends Partial<EventInterface> {
  selectedTimeZone: string
  onTimeZoneSelect?: (timeZone: string) => void
  selectedLocationId?: EventLocationInterface['id']
  onLocationSelect?: (locationId: EventLocationInterface['id']) => void
  titlesColor?: string
  mobileTitlesColor?: string
  color?: string
  mobileColor?: string
  isLoading?: boolean
  selectedSlotBackgroundColor: string
  booking?: Partial<BookingInterface>
  timeFormat?: TimeFormatEnum
  isMobile?: boolean
}

export const EDITOR_DURATION_PLACEHOLDER = 30

export const EventInfo = ({
  name,
  description = '',
  eventDuration,
  locations,
  selectedTimeZone,
  onTimeZoneSelect,
  selectedLocationId,
  onLocationSelect,
  displayTimezoneType,
  hostName,
  hostAvatarUrl,
  titlesColor,
  mobileTitlesColor,
  color,
  mobileColor,
  selectedSlotBackgroundColor,
  isLoading,
  booking,
  timeFormat,
  isMobile,
}: EventInfoProps) => {
  const timeZonesList = getAllTimeZones(timeFormat)

  const { t } = useTypedTranslation()

  // --NOTE:  we disable radio buttons, because can't change the location on the reschedule page
  // https://systeme-team.slack.com/archives/C07EM2BE495/p1744053447693679?thread_ts=1743515917.836379&cid=C07EM2BE495
  const isLocationRadioGroupDisabled = booking ? true : !onLocationSelect
  if (isLoading) return <EventInfoSkeleton />

  return (
    <EventInfoWrapperUi
      titlesColor={isMobile ? mobileTitlesColor : titlesColor}
      color={isMobile ? mobileColor : color}
    >
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              borderRadius: '50%',
              overflow: 'hidden',
              objectFit: 'cover',
              height: 24,
            }}
          >
            <img
              src={
                hostAvatarUrl ||
                '/assets/images/default_post_author_avatar.webp'
              }
              alt={hostName}
              width={24}
            />
          </div>
          <span>{hostName}</span>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            margin: '8px 0 16px',
          }}
        >
          <div>
            <span style={{ fontSize: '16px', fontWeight: 600 }}>
              {name || t('components.core.calendar.empty_event_name')}
            </span>
            <CalendarEventDescriptionUi
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />
          </div>
          <LineSeparator />
          {!!booking && booking.date && (
            <>
              <div>
                <span>{t('components.core.calendar.booking_former_time')}</span>
                <BookingDate
                  date={new Date(booking.date)}
                  eventDuration={eventDuration || EDITOR_DURATION_PLACEHOLDER}
                />
              </div>
              <LineSeparator />
            </>
          )}
          <div>
            <p style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <HourglassIcon fill={selectedSlotBackgroundColor} />
              {eventDuration || EDITOR_DURATION_PLACEHOLDER}{' '}
              {t('components.core.calendar.min')}
            </p>
          </div>
          <LineSeparator />
          <div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {!locations ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <GoogleMeetIcon fill={selectedSlotBackgroundColor} />
                  <p>
                    {t(
                      getReadableEventLocation(
                        EventLocationTypeEnum.OnlineMeeting,
                      ),
                    )}
                  </p>
                </div>
              ) : (
                locations?.map(({ locationType, id }) => {
                  const showRadioButtons = locations.length > 1
                  const Icon = getReadableEventLocationIcon(locationType)

                  return showRadioButtons ? (
                    <label
                      key={locationType}
                      htmlFor={`location-type-${locationType}`}
                      style={{
                        opacity: isLocationRadioGroupDisabled ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-center',
                        gap: '6px',
                      }}
                    >
                      <InputRadio
                        type="radio"
                        id={`location-type-${locationType}`}
                        checked={selectedLocationId === id}
                        disabled={isLocationRadioGroupDisabled}
                        onChange={() => onLocationSelect?.(id)}
                        checkedColor={selectedSlotBackgroundColor}
                      />
                      <p>{t(getReadableEventLocation(locationType))}</p>
                    </label>
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                      key={locationType}
                    >
                      <Icon fill={selectedSlotBackgroundColor} />
                      <p>{t(getReadableEventLocation(locationType))}</p>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <WorldIcon2 fill={titlesColor} />
          <EventTimeZoneSelectWrapper>
            <Select
              isDisabled={
                !onTimeZoneSelect ||
                displayTimezoneType === EventTimeZoneTypeEnum.Fixed
              }
              options={timeZonesList}
              styles={{
                control: provided => ({
                  ...provided,
                  border: 0,
                  backgroundColor: 'transparent',
                  fontSize: 14,
                }),
                indicatorSeparator: () => ({}),
              }}
              value={{
                label: `${selectedTimeZone} (${getTimeZoneTime(selectedTimeZone, timeFormat)})`,
                value: selectedTimeZone,
              }}
              onChange={timeZone => {
                if (timeZone && onTimeZoneSelect) {
                  onTimeZoneSelect?.(timeZone.value)
                }
              }}
            />
          </EventTimeZoneSelectWrapper>
        </div>
      </div>
    </EventInfoWrapperUi>
  )
}

function BookingDate({
  date,
  eventDuration,
}: {
  date: Date
  eventDuration: number
}) {
  const { t } = useTypedTranslation()

  const startTime = date.toTimeString().slice(0, 5)
  date.setMinutes(date.getMinutes() + eventDuration)
  const endTime = date.toTimeString().slice(0, 5)

  return (
    <>
      <p>
        {startTime} - {endTime}
      </p>
      <p>{formatDate(date, t)}</p>
    </>
  )
}

function LineSeparator() {
  return (
    <div
      style={{
        margin: '16px 0',
        height: '1px',
        width: '100%',
        backgroundColor: '#E4E5E7',
      }}
    ></div>
  )
}
